import { useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom-v5-compat";
import { useModuleBEM } from "@b2bportal/core-themes";
import { LazyLoading } from "@b2bportal/core-ui";
import { HotelNavigationUtility } from "@b2bportal/core-utilities";
import type { GuestsSelection } from "@b2bportal/lodging-api";
import type { Tile } from "@b2bportal/rbc-deal-tiles-api";
import { getPercentage, useI18nContext } from "@hopper-b2b/i18n";

import { HOTELS_AVAILABILITY_PATH } from "@overrides/utilities";
import { Experiments, useExperiment } from "@tenant/experiments";
import { HotelCard } from "@tenant/hotels";
import dayjs from "dayjs";
import styles from "./HotelFundedOffersPage.module.scss";
import { OfferCardSkeleton } from "./OfferCardSkeleton";
import { OfferEligibleBanner } from "./OfferEligibleBanner";
import { OfferHotelLink } from "./OfferHotelLink";
import { rankOfferLodgings } from "./offerRanking";
import { OfferSearchBar, type OfferSearchValues } from "./OfferSearchBar";
import { OffersPagination } from "./OffersPagination";
import { OffersStatusScreen } from "./OffersStatusScreen";
import { OfferTerms } from "./OfferTerms";
import { getPageCount, getPageItems, PAGE_SIZE } from "./pagination";

import { getOfferImageSrc } from "@/modules/hotel-funded-offers/offerImage";
import { getDefaultOfferStayDates } from "@/modules/hotel-funded-offers/offerSearch";
import {
  useFetchHotelsForTileQuery,
  useFetchOfferAvailabilityQuery,
} from "@/modules/hotel-funded-offers/store";
import { PromoBanner } from "@/modules/wallet";
import {
  HOTEL_DEALS_OFFER_ID,
  HOTEL_DEALS_OFFER_NAME,
  OfferEntrySource,
  OfferFunnelEntrySource,
  OfferRawEventName,
  OfferType,
  setOfferFunnelEntrySource,
  useEmitRbcRawEvent,
  useEmitRbcRawEventOnce,
} from "@/tracking/offers";

export const HotelFundedOffersPage = () => {
  const [block, cn] = useModuleBEM(styles, "HotelFundedOffersPage");
  const { t, locale } = useI18nContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const routerLocation = useLocation();
  const bodyRef = useRef<HTMLDivElement>(null);

  const tileFromState = (routerLocation.state as { tile?: Tile } | null)?.tile;

  const marketId = searchParams.get("marketId") ?? "";
  const countryCode = searchParams.get("countryCode") ?? "";
  const hasMarket = marketId !== "" && countryCode !== "";

  // Read travelers/rooms/dates from the URL with the same parser the
  // availability/shop pages use, so guests (incl. children-age unification) and
  // rooms are interpreted identically. Dates fall back to the placeholder stay
  // window for bare/direct loads.
  const {
    adults,
    children,
    rooms,
    fromDate: parsedFromDate,
    untilDate: parsedUntilDate,
  } = HotelNavigationUtility.parseLodgingParams(searchParams);
  const defaultDates = getDefaultOfferStayDates();
  const fromDate = parsedFromDate || defaultDates.fromDate;
  const untilDate = parsedUntilDate || defaultDates.untilDate;
  const guests: GuestsSelection = { adults, children };

  const { isExperimentVariant, data: experimentsData } = useExperiment();
  const enabled = isExperimentVariant(
    Experiments.HOTEL_FUNDED_OFFERS.name,
    Experiments.HOTEL_FUNDED_OFFERS.variants.available
  );

  const {
    data: hotelsForTileData,
    isLoading: isLoadingLodgingIds,
    isError: isLodgingIdsError,
  } = useFetchHotelsForTileQuery(
    { byMarket: { marketId, countryCode } },
    { skip: !enabled || !hasMarket }
  );

  const lodgingIds = hotelsForTileData?.lodgingIds ?? [];

  // Prefer the tile from navigation state; only fall back to the hotelsForTile
  // response when it's missing (e.g. the user refreshed).
  const tile = tileFromState ?? hotelsForTileData?.tile;
  const location = tile?.label ?? "";
  const savings = tile?.savingsPercentage ?? 0;
  const percent = getPercentage(savings * 100, locale);
  const promoImageSrc = getOfferImageSrc(tile?.image?.x2);

  const {
    data: availability,
    isFetching: isFetchingAvailability,
    isError: isAvailabilityError,
  } = useFetchOfferAvailabilityQuery(
    { lodgingIds, fromDate, untilDate, guests, rooms },
    { skip: lodgingIds.length === 0 }
  );

  const lodgings = useMemo(
    () => rankOfferLodgings(availability?.lodgings ?? []),
    [availability]
  );

  const emitTappedSearch = useEmitRbcRawEvent(
    OfferRawEventName.TappedOfferSearch
  );
  const emitTappedSeeAll = useEmitRbcRawEvent(
    OfferRawEventName.TappedSeeAllHotels
  );

  useEmitRbcRawEventOnce(
    {
      name: OfferRawEventName.OffersEntry,
      properties: {
        offer_type: OfferType.hotel_merch,
        offer_id: HOTEL_DEALS_OFFER_ID,
        offer_name: HOTEL_DEALS_OFFER_NAME,
        destination: marketId,
        destination_label: location,
        destination_country_code: countryCode,
        entry_source: tileFromState
          ? OfferEntrySource.offer_tile
          : OfferEntrySource.deeplink,
        is_claimable: false,
      },
    },
    tile != null && hasMarket
  );

  useEffect(() => {
    if (tile == null || !hasMarket) return;
    setOfferFunnelEntrySource(OfferFunnelEntrySource.offer_page);
  }, [tile, hasMarket]);

  // Client-side pagination over the fully-loaded offer list.
  const [page, setPage] = useState(1);
  // Reset to the first page whenever a new search changes the result set.
  useEffect(() => {
    setPage(1);
  }, [fromDate, untilDate, rooms, adults, children.length]);

  const pageCount = getPageCount(lodgings.length);
  const safePage = Math.min(page, Math.max(pageCount, 1));
  const pageItems = getPageItems(lodgings, safePage);

  const goToPage = (next: number) => {
    setPage(next);
    // Scroll to the top of the results body (below the promo banner)
    bodyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Don't redirect while experiments are unresolved (direct page loads render
  // before the query returns; isExperimentVariant is false until then).
  if (experimentsData == null) {
    return <LazyLoading />;
  }
  if (!enabled || !hasMarket) {
    return <Navigate to="/" replace />;
  }
  // On refresh/direct load there's no tile in navigation state, so the header
  // and promo depend on the hotelsForTile response. Block on a full-page loader
  // until the tile resolves (unless the request errored — then fall through to
  // the in-page error state).
  if (tile == null && !isLodgingIdsError) {
    return <LazyLoading />;
  }

  const isLoading = isLoadingLodgingIds || isFetchingAvailability;
  const isError = isLodgingIdsError || isAvailabilityError;
  const dateRange = { from: fromDate, until: untilDate };

  // Commit a new search to the URL, which drives the availability query.
  const handleSearch = ({
    dateRange: nextDateRange,
    guests: nextGuests,
    rooms: nextRooms,
  }: OfferSearchValues) => {
    const checkIn = nextDateRange.from ?? fromDate;
    const checkOut = nextDateRange.until ?? untilDate;
    const los = dayjs(checkOut).diff(dayjs(checkIn), "day");
    const advance = dayjs(checkIn)
      .startOf("day")
      .diff(dayjs().startOf("day"), "day");
    emitTappedSearch({
      properties: {
        offer_id: HOTEL_DEALS_OFFER_ID,
        offer_name: HOTEL_DEALS_OFFER_NAME,
        offer_type: OfferType.hotel_merch,
        destination: marketId,
        check_in_date: dayjs(checkIn).toISOString(),
        check_out_date: dayjs(checkOut).toISOString(),
        los: los > 0 ? los : undefined,
        advance: advance >= 0 ? advance : undefined,
        adults_count: nextGuests.adults,
        children_count: nextGuests.children.length,
        is_claimable: false,
      },
    });

    const next = HotelNavigationUtility.buildSearchParams({
      dateRange: {
        from: nextDateRange.from ?? fromDate,
        until: nextDateRange.until ?? untilDate,
      },
      guests: nextGuests,
      roomsCount: nextRooms,
    });
    ["marketId", "countryCode"].forEach((key) => {
      const value = searchParams.get(key);
      if (value != null) {
        next.set(key, value);
      }
    });
    // Preserve the navigation-state tile so re-searching doesn't drop it back to
    // the (cached) response.
    setSearchParams(next, { state: routerLocation.state });
  };

  const getSeeAllHotelsPath = () => {
    if (!location) return undefined;
    const params = HotelNavigationUtility.buildSearchParams({
      dateRange: { from: fromDate, until: untilDate },
      guests,
      roomsCount: rooms,
    });
    const destinationCode = HotelNavigationUtility.placeLabelToUrl(location);
    return `${HOTELS_AVAILABILITY_PATH}/${destinationCode}?${params.toString()}`;
  };
  const seeAllHotelsPath = getSeeAllHotelsPath();

  const emitSeeAllTapped = () =>
    emitTappedSeeAll({
      properties: {
        offer_id: HOTEL_DEALS_OFFER_ID,
        offer_name: HOTEL_DEALS_OFFER_NAME,
        offer_type: OfferType.hotel_merch,
        destination: marketId,
        is_claimable: false,
      },
    });

  // The results body resolves to exactly one state: loading shows skeleton
  // cards; otherwise error, empty, or the paginated results.
  const renderBodyContent = () => {
    if (isLoading) {
      return (
        <div className={cn("list")} aria-busy="true">
          {Array.from({ length: PAGE_SIZE }, (_, index) => (
            <OfferCardSkeleton key={index} />
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <OffersStatusScreen className={cn("status-screen")} variant="error" />
      );
    }
    if (lodgings.length === 0) {
      return (
        <OffersStatusScreen
          className={cn("status-screen")}
          variant="no-availability"
          location={location}
          searchAllStaysHref={seeAllHotelsPath}
          onSearchAllStays={emitSeeAllTapped}
        />
      );
    }
    return (
      <>
        <div className={cn("list")}>
          {pageItems.map((lodging) => (
            <OfferHotelLink
              key={lodging.lodging.id}
              className={cn("card-link")}
              hotel={lodging}
              dateRange={dateRange}
              guests={guests}
              rooms={rooms}
            >
              <HotelCard
                hotel={lodging}
                dateRange={dateRange}
                rooms={rooms}
                stacked
              />
            </OfferHotelLink>
          ))}
        </div>
        <div className={cn("pagination-container")}>
          <OffersPagination
            className={cn("pagination")}
            page={safePage}
            pageCount={pageCount}
            onPageChange={goToPage}
          />
          {seeAllHotelsPath && (
            <Link
              className={cn("see-all")}
              to={seeAllHotelsPath}
              onClick={emitSeeAllTapped}
            >
              {t("hotelFundedOffersPage.seeAllHotels", { location })}
            </Link>
          )}
        </div>
      </>
    );
  };

  return (
    <div className={block}>
      {location ? (
        <PromoBanner
          className={cn("promo")}
          imageUrl={promoImageSrc}
          imageAlt={location}
          title={t("hotelFundedOffersPage.promoTitle", { percent, location })}
          description={t("hotelFundedOffersPage.promoDescription")}
        />
      ) : null}

      <div className={cn("body")} ref={bodyRef}>
        <div className={cn("header")}>
          <div className={cn("title-container")}>
            {location ? (
              <h1 className={cn("title")}>
                {t("hotelFundedOffersPage.title", { location })}
              </h1>
            ) : null}
            {savings ? (
              <p className={cn("subtitle")}>
                {t("hotelFundedOffersPage.subtitle", { percent })}
              </p>
            ) : null}
          </div>

          <OfferSearchBar
            initialValues={{ dateRange, guests, rooms }}
            onSearch={handleSearch}
          />

          {!isLoading && !isError && seeAllHotelsPath && (
            <OfferEligibleBanner
              className={cn("banner")}
              searchAllStaysHref={seeAllHotelsPath}
              onSearchAllStays={emitSeeAllTapped}
            />
          )}
        </div>

        {renderBodyContent()}
      </div>

      {location ? <OfferTerms /> : null}
    </div>
  );
};
